import * as Sentry from "@sentry/nextjs";

import { getContainerParameters } from "./isWrappable";

import type { ActiveContainerFrame, Atom, GroupSlice, WrappableContainer, RowNode } from "./types";
import type { InlineNodeContentRenderItem } from "../types";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

type Frame = {
  container: WrappableContainer | null;
  parameterIndex: number;
  openSeen: boolean;
  closeSeen: boolean;
  children: RowNode[];
};

/**
 * Reconstructs the nested RowNode tree for a single row.
 *
 * @param rowAtoms - atoms belonging to this row, in order
 * @param activeAtStart - containers that were opened on a previous row and are still open
 * at the start of this row (innermost last). Each frame carries its
 * `parameterIndex` so a continuation row resumes inside the right parameter
 * inline.
 * @param topLevelInline - the inline node whose items live at depth 0; used to set
 * `parentInline` on top-level items runs for cursor wiring
 */
export function buildRowTree(
  rowAtoms: Atom[],
  activeAtStart: ActiveContainerFrame[],
  topLevelInline?: InlineNodeModel,
): RowNode[] {
  const root: Frame = {
    container: null,
    parameterIndex: 0,
    openSeen: false,
    closeSeen: false,
    children: [],
  };
  const stack: Frame[] = [root];

  for (const active of activeAtStart) {
    stack.push({
      container: active.container,
      parameterIndex: active.parameterIndex,
      openSeen: false,
      closeSeen: false,
      children: [],
    });
  }

  for (const atom of rowAtoms) {
    if (atom.kind === "open") {
      stack.push({
        container: atom.container,
        parameterIndex: 0,
        openSeen: true,
        closeSeen: false,
        children: [],
      });
      continue;
    }

    if (atom.kind === "close") {
      const frame = stack[stack.length - 1];
      if (!frame.container) {
        Sentry.captureMessage("buildRowTree: unbalanced close atom (no matching open)", {
          level: "error",
          extra: {
            containerType: atom.container.type,
          },
        });
        console.warn("buildRowTree: unbalanced close atom (no matching open)", atom);
        continue;
      }
      stack.pop();
      frame.closeSeen = true;

      const groupNode = frameToGroupNode(frame);

      if (groupNode) {
        appendChild(stack[stack.length - 1], groupNode);
      }
      continue;
    }

    if (atom.kind === "comma") {
      const frame = stack[stack.length - 1];

      appendChild(frame, {
        kind: "comma",
        container: atom.container,
        parameterIndex: atom.parameterIndex,
      });
      frame.parameterIndex += 1;
      continue;
    }

    if (atom.kind === "empty-parameter") {
      const frame = stack[stack.length - 1];
      frame.children.push({ kind: "items", items: [], parentInline: atom.parentInline });
      continue;
    }

    if (atom.kind === "item") {
      appendItem(stack[stack.length - 1], atom.item, topLevelInline);
    }
  }

  while (stack.length > 1) {
    const frame = stack.pop()!;
    if (!frame.container) break;

    const groupNode = frameToGroupNode(frame);

    if (groupNode) {
      appendChild(stack[stack.length - 1], groupNode);
    }
  }

  return root.children;
}

function frameToGroupNode(frame: Frame): RowNode | null {
  if (!frame.container) {
    Sentry.captureMessage("frameToGroupNode: frame has no container", { level: "error" });
    console.warn("frameToGroupNode: frame has no container");
    return null;
  }

  return {
    kind: "group",
    container: frame.container,
    slice: sliceOf(frame),
    children: frame.children,
  };
}

function sliceOf(frame: Frame): GroupSlice {
  if (frame.openSeen && frame.closeSeen) {
    return "whole";
  }

  if (frame.openSeen) {
    return "open";
  }

  if (frame.closeSeen) {
    return "close";
  }

  return "middle";
}

function appendChild(frame: Frame, child: RowNode): void {
  frame.children.push(child);
}

function appendItem(
  frame: Frame,
  item: InlineNodeContentRenderItem,
  topLevelInline: InlineNodeModel | undefined,
): void {
  const parentInline = resolveParentInline(frame, topLevelInline);
  const last = frame.children.at(-1);

  if (last && last.kind === "items" && last.parentInline === parentInline) {
    last.items.push(item);
    return;
  }

  frame.children.push({ kind: "items", items: [item], parentInline });
}

function resolveParentInline(
  frame: Frame,
  topLevelInline: InlineNodeModel | undefined,
): InlineNodeModel | undefined {
  if (frame.container) {
    const params = getContainerParameters(frame.container);
    return params[frame.parameterIndex];
  }

  return topLevelInline;
}

export function bracketsFromSlice(slice: GroupSlice) {
  return {
    showLeft: slice === "whole" || slice === "open",
    showRight: slice === "whole" || slice === "close",
  };
}
